import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Table, TableBody, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import PropTypes from 'prop-types';
import { EventContentTableRow } from '../EventContentTableRow';
import formatter from 'app/utils/formatter';
import renderers from './renderers';

// Context to provide previousEventDifferences to all nested components
const EventDifferencesContext = React.createContext(null);

// Custom hook to access the context
export const useEventDifferences = () => {
  return React.useContext(EventDifferencesContext);
};

const EventContentTable = ({ event, ignoreKeys, orderKeys, keyRenderers, collapse }) => {
  const [expanded, setExpanded] = React.useState(false);
  const { type, previousEventDifferences } = event;

  const contextDifferences = useEventDifferences();
  const activeDifferences = previousEventDifferences || contextDifferences;

  // Memoize the key change checker for performance
  const hasKeyChanged = React.useCallback(
    key => {
      if (!activeDifferences) return false;

      return Object.keys(activeDifferences).some(diffKey => diffKey.endsWith(`.${key}`) || diffKey === key);
    },
    [activeDifferences],
  );

  // Memoize filtered keys to avoid recalculation
  const filteredKeys = React.useMemo(() => {
    const allKeys = Object.keys(event).filter(key => !ignoreKeys.includes(key));
    const validOrderKeys = orderKeys || [];
    const orderedKeys = validOrderKeys.filter(key => allKeys.includes(key));
    const remainingKeys = allKeys.filter(key => !validOrderKeys.includes(key));
    return [...orderedKeys, ...remainingKeys];
  }, [event, ignoreKeys, orderKeys]);

  const renderEventKey = React.useCallback(
    key => {
      const renderer = key === 'value' && type === 'wage' ? keyRenderers.wage : keyRenderers[key];
      const hasChanged = hasKeyChanged(key);

      return (
        <EventContentTableRow key={key} header={key} value={event[key]} renderer={renderer} hasChanged={hasChanged} />
      );
    },
    [event, type, keyRenderers, hasKeyChanged],
  );

  const tableContent = React.useMemo(
    () => (
      <Table>
        <TableBody>{filteredKeys.map(renderEventKey)}</TableBody>
      </Table>
    ),
    [filteredKeys, renderEventKey],
  );

  const accordionContent = React.useMemo(
    () => (
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} />
        <AccordionDetails>{tableContent}</AccordionDetails>
      </Accordion>
    ),
    [expanded, tableContent],
  );

  const content = collapse ? accordionContent : tableContent;

  // Only provide context if this component has previousEventDifferences and no context exists
  if (previousEventDifferences && !contextDifferences) {
    return (
      <EventDifferencesContext.Provider value={previousEventDifferences}>{content}</EventDifferencesContext.Provider>
    );
  }

  return content;
};

EventContentTable.propTypes = {
  event: PropTypes.object.isRequired,
  keyRenderers: PropTypes.object,
  collapse: PropTypes.bool,
  ignoreKeys: PropTypes.array,
  orderKeys: PropTypes.array,
};

EventContentTable.defaultProps = {
  collapse: false,
  ignoreKeys: [
    '@id',
    'mongoId',
    'connectionId',
    '@type',
    'eventType',
    'previousEventDifferences',
    'hrEmployee',
    'batch',
    'batchId',
    'externalId',
  ],
  orderKeys: ['employeeName', 'eventDate', 'processedYear', 'processedMonth'],
  keyRenderers: {
    correction: formatter.yesNoEmpty,
    wage: formatter.currency,
    annualWage: formatter.currency,
    grossYearlyFullTimeComponentWage: formatter.currency,
    grossYearlyFullTimeComponentWagePrevious: formatter.currency,
    hrEmployee: value => <span>{`${value.personName.fullName} ${value.syncStatus && `(${value.syncStatus})`}`}</span>,
    processed: value => (value ? <CheckIcon /> : <ClearIcon />),
    archived: value => (value ? <CheckIcon /> : <ClearIcon />),
    periodStart: formatter.date,
    periodEnd: formatter.date,
    periodWage: formatter.currency,
    employeeCategory: renderers.employeeCategory,
    hourlyWage: formatter.currency,
    cumulativeWage: formatter.currency,
    createdAt: formatter.dateTime,
    eventDate: formatter.systemDate,
    startDate: formatter.systemDate,
    dateStart: formatter.date,
    endDate: formatter.systemDate,
    dateOfBirth: formatter.date,
    startDateOfEmployment: formatter.date,
    endDateOfEmployment: formatter.date,
    employmentStart: formatter.systemDate,
    employmentEnd: formatter.systemDate,
    probationDate: formatter.date,
    percentageStartDate: formatter.date,
    relationStartDate: formatter.date,
    relationEndDate: formatter.date,
    grossWage: formatter.currency,
    svWage: formatter.currency,
    aofWkoPremium: formatter.currency,
    children: value =>
      value.length > 0 &&
      value.map(child => (
        <EventContentTable key={child['@id']} event={child} ignoreKeys={['@key', '@type', '@id']} collapse />
      )),
    address: value =>
      value && (
        <>
          <Typography component="p">
            <span>{`${value.street} ${value.houseNumber}`}</span>
            {value.houseNumberSuffix && <span>{` ${value.houseNumberSuffix}`}</span>}
          </Typography>
          <Typography component="p">{`${value.zipCode} ${value.city}`}</Typography>
          <Typography>{value.country}</Typography>
        </>
      ),
    previousAnnualWage: formatter.currency,
    previousDateStart: formatter.date,
  },
};

export default EventContentTable;
